import { inFrames as f } from 'scripts/utils/inFrames';
import { toRadians as r } from './utils';
import { getTransition as t } from './utils/transition';
import type { ObjectsStates, Transition, TransitionArgs } from './types/animations';
import type { AnimationEvent } from './types/animations';
import type { MaterialFields } from './types/material';
import type { LightsFields } from './types/lights';

export namespace HomeHero {

    export enum Objects {
        Camera = 'Camera',

        HeartsRoot = 'heart1',
        HeartsFloat = 'heart_float',
        HeartsHover1 = 'heart4',
        HeartsHover2 = 'heart 2',
        HeartsHover3 = 'heart 3',

        GraphRoot = 'graph',
        GraphFloat = 'graph_float',

        DashboardRoot = 'Dashboard',
        DashboardFloat = 'dash_float',
        DashboardHover1 = 'Dashboard1',
        DashboardHover2 = 'Capsule_2',

        PromotionsRoot = 'Music_promotions',
        PromotionsFloat = 'mp_float',
        PromotionsHover1 = 'Music_promotions1',
        PromotionsHover2 = 'bar_1',
        PromotionsHover3 = 'bar_2',

        HeadphonesRoot = 'headphones',
        HeadphonesFloat = 'hp_float',
        HeadphonesHover = 'headphones1',
    }

    export namespace Colors {
        export type Keys = 'violet' | 'green' | 'blue' | 'orange';
        export type Map = Record<'violet' | 'green' | 'blue' | 'orange', number | string>;

        export const BackfaceExternal = '#8343F6';

        export const Backface: Map = {
            violet: 0xD3BEFD,
            green: 0xD6E2B2,
            blue: 0xBEDBFD,
            orange: 0xE2CCB2,
        };

        const basePrimary = {
            emissiveIntensity: 0.55,
            shininess: 1,
            reflectivity: 1,
        };

        export const Primary = {
            violet: {
                ...basePrimary,
                color: '#ca71e8',
                specular: '#eccdf5',
                emissive: '#8a66ff',
            },
            green: {
                ...basePrimary,
                color: '#b7c47e',
                specular: '#e7efd4',
                emissive: '#9eb45d',
            },
            blue: {
                ...basePrimary,
                color: '#6fc7fc',
                specular: '#a7e4f5',
                emissive: '#0095ff',
            },
            orange: {
                ...basePrimary,
                color: '#c1ab5a',
                specular: '#fffdf4',
                emissive: '#ffb641',
            },
        } satisfies Record<Keys, MaterialFields.MeshPhongRepresentation>;

        const baseSecondary = {
            emissiveIntensity: 1,
            metalness: 0,
            roughness: 0.55,
            reflectivity: 0.8,
            clearcoat: 0,
            clearcoatRoughness: 1,
        };

        export const Secondary = {
            violet: {
                ...baseSecondary,
                color: '#bb83fc',
                emissive: '#b7aede',
            },
            green: {
                ...baseSecondary,
                color: '#e4efd2',
                emissive: '#a1b16f',
            },
            blue: {
                ...baseSecondary,
                color: '#ffffff',
                emissive: '#7ca3de',
            },
            orange: {
                ...baseSecondary,
                color: '#f2f1e6',
                emissive: '#c4a779',
            },
        } satisfies Record<Keys, MaterialFields.MeshPhysicalRepresentation>;

        const PrimaryLightDefault = () => ({
            color: '#ffffff',
            intensity: 2.2,
        } as const);

        export const PrimaryLight = {
            violet: PrimaryLightDefault(),
            green: PrimaryLightDefault(),
            blue: PrimaryLightDefault(),
            orange: PrimaryLightDefault(),
        } satisfies Record<Keys, LightsFields.Representation>;

        const SecondaryLightDefault = () => ({
            color: '#ffffff',
            intensity: 2.2,

        } as const);

        export const SecondaryLight = {
            violet: SecondaryLightDefault(),
            green: SecondaryLightDefault(),
            blue: SecondaryLightDefault(),
            orange: SecondaryLightDefault(),
        } satisfies Record<Keys, LightsFields.Representation>;

        export const Background: Map = {
            violet: '#F3EFFD',
            green: '#FAFFF9',
            blue: '#F9FCFF',
            orange: '#FFFCF9',
        };
    }

    export namespace States {
        export const BaseState = 'BaseState' as const;
        export const State = 'State' as const;
        export const State2 = 'State 2' as const;

        export namespace Camera {
            export enum Keys {
                Offscreen = '%Offscreen',
                Initial = '%Initial',
            }

            export const root = {
                name: Objects.Camera,
                states: {
                    Offscreen: {
                        position: [0, Keys.Offscreen, 1000],
                    },
                    Initial: {
                        position: [0, Keys.Initial, 1000],
                    },
                    Default: {
                        position: [0, 0, 1000],
                    },
                },
            } as const satisfies ObjectsStates;
        }

        export namespace Hearts {
            export const root = {
                name: Objects.HeartsRoot,
                states: {
                    [BaseState]: {
                        position: { x: 0, y: 0, z: 0 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: r(-14.43), z: 0 },
                    },
                    State: {
                        position: { x: -223.77, y: -204.82, z: -57.55 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: r(-14.43), z: 0 },
                    },
                    [States.State2]: {
                        position: { x: 0, y: 0, z: 0 },
                        scale: { x: 0, y: 0, z: 0 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;

            export const float = {
                name: Objects.HeartsFloat,
                states: {
                    [BaseState]: {
                        position: { x: 0, y: 0, z: 0 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: -17.94, y: -26.43, z: 0 },
                        rotation: { x: r(14.7), y: r(16.12), z: r(-7.8) },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover1 = {
                name: Objects.HeartsHover1,
                states: {
                    [BaseState]: {
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        scale: { x: 1.2, y: 1.2, z: 1.2 },
                        rotation: { x: 0, y: r(12), z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover2 = {
                name: Objects.HeartsHover2,
                states: {
                    [BaseState]: {
                        position: { x: 0, y: 0, z: 0 },
                        scale: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: 88.15, y: 116.58, z: -47.95 },
                        scale: { x: 0.5, y: 0.5, z: 0.5 },
                    },
                    [States.State2]: {
                        position: { x: 109.28, y: 154.08, z: -47.95 },
                        scale: { x: 0, y: 0, z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover3 = {
                name: Objects.HeartsHover3,
                states: {
                    [BaseState]: {
                        position: { x: 0, y: 0, z: 0 },
                        scale: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: -34.70, y: 133.44, z: -145.25 },
                        scale: { x: 0.5, y: 0.5, z: 0.5 },
                    },
                    [States.State2]: {
                        position: { x: -49.17, y: 179.07, z: -145.25 },
                        scale: { x: 0, y: 0, z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;
        }

        export namespace Graph {
            export const root = {
                name: Objects.GraphRoot,
                states: {
                    [BaseState]: {
                        position: { x: 0, y: -34, z: 200 },
                        scale: { x: 0.90, y: 0.90, z: 0.90 },
                        rotation: { x: r(-7.89), y: r(-14.33), z: r(-8.96) },
                    },
                    State: {
                        position: { x: 0, y: 18.22, z: 200 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    [States.State2]: {
                        position: { x: 60, y: 18.22, z: 200 },
                        scale: { x: 4.50, y: 4.50, z: 4.50 },
                        rotation: { x: r(-8), y: r(180), z: r(8) },
                    },
                },
            } as const satisfies ObjectsStates;

            export const float = {
                name: Objects.GraphFloat,
                states: {
                    [BaseState]: {
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        rotation: { x: r(2.64), y: r(-4.61), z: r(0.21) },
                    },
                },
            } as const satisfies ObjectsStates;
        }

        export namespace Dashboard {
            export const root = {
                name: Objects.DashboardRoot,
                states: {
                    [BaseState]: {
                        position: { x: -560.21, y: 5, z: 11.14 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: -106.69, y: -137.84, z: 11.14 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    [States.State2]: {
                        position: { x: -106.69, y: -137.84, z: 11.14 },
                        scale: { x: 0, y: 0, z: 0 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;

            export const float = {
                name: Objects.DashboardFloat,
                states: {
                    [BaseState]: {
                        position: { x: 1.62, y: -0.53, z: -1.42 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: 36.79, y: 27.31, z: -1.42 },
                        rotation: { x: r(7.7), y: r(10.07), z: r(3.5) },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover1 = {
                name: Objects.DashboardHover1,
                states: {
                    [BaseState]: {
                        position: { x: -5.60, y: -6.96, z: -2.02 },
                        scale: { x: 2.30, y: 2.30, z: 2.30 },
                        rotation: { x: r(159.36), y: r(-62.86), z: r(167.23) },
                    },
                    State: {
                        position: { x: -5.60, y: -6.96, z: -2.02 },
                        scale: { x: 3, y: 3, z: 3 },
                        rotation: { x: r(152.6), y: r(-49.84), z: r(161.83) },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover2 = {
                name: Objects.DashboardHover2,
                states: {
                    [BaseState]: {
                        rotation: { x: r(90), y: r(-65.83), z: 0 },
                    },
                    State: {
                        rotation: { x: r(90), y: r(16.17), z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;
        }

        export namespace Promotions {
            export const root = {
                name: Objects.PromotionsRoot,
                states: {
                    [BaseState]: {
                        position: { x: -440.24, y: 309.70, z: -0.05 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: -94.13, y: 68.93, z: -0.05 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    [States.State2]: {
                        position: { x: -94.13, y: 68.93, z: -0.05 },
                        scale: { x: 0, y: 0, z: 0 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;

            export const float = {
                name: Objects.PromotionsFloat,
                states: {
                    [BaseState]: {
                        position: { x: 0.09, y: 0.03, z: -1.39 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: 34.38, y: -28.90, z: -1.39 },
                        rotation: { x: r(-13.82), y: r(-8.44), z: r(5.53) },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover1 = {
                name: Objects.PromotionsHover1,
                states: {
                    [BaseState]: {
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: r(-7), z: 0 },
                    },
                    State: {
                        scale: { x: 1.2, y: 1.2, z: 1.2 },
                        rotation: { x: 0, y: r(-20), z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover2 = {
                name: Objects.PromotionsHover2,
                states: {
                    [BaseState]: {
                        position: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: 9.61, y: 0, z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover3 = {
                name: Objects.PromotionsHover3,
                states: {
                    [BaseState]: {
                        position: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: 20.93, y: 0, z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;
        }

        export namespace Headphones {
            export const root = {
                name: Objects.HeadphonesRoot,
                states: {
                    [BaseState]: {
                        position: { x: 621.80, y: 5, z: 7.57 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: 245.82, y: -112.00, z: 7.57 },
                        scale: { x: 1, y: 1, z: 1 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    [States.State2]: {
                        position: { x: 245.82, y: -112.00, z: 7.57 },
                        scale: { x: 0, y: 0, z: 0 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                },
            } as const satisfies ObjectsStates;

            export const float = {
                name: Objects.HeadphonesFloat,
                states: {
                    [BaseState]: {
                        position: { x: -4.29, y: 3.92, z: 2.32 },
                        rotation: { x: 0, y: 0, z: 0 },
                    },
                    State: {
                        position: { x: -27.69, y: 23.79, z: -21.96 },
                        rotation: { x: r(-7.48), y: r(-8.03), z: r(-8.79) },
                    },
                },
            } as const satisfies ObjectsStates;

            export const hover = {
                name: Objects.HeadphonesHover,
                states: {
                    [BaseState]: {
                        position: { x: 2.43, y: -29.42, z: -0.34 },
                        scale: { x: 2, y: 2, z: 2 },
                        rotation: { x: r(-14.65), y: r(-66.9), z: r(-11.06) },
                    },
                    State: {
                        position: { x: 2.43, y: -29.42, z: -0.34 },
                        scale: { x: 2, y: 2, z: 2 },
                        rotation: { x: r(171.06), y: r(45.98), z: r(-173.78) },
                    },
                },
            } as const satisfies ObjectsStates;
        }
    }

    export namespace Events {
        export const Scroll = {
            transitions: [
                t(States.Camera.root, 'Initial', 'Default', { position: 0, duration: 200, easing: 'power.inOut' }),

                t(States.Promotions.root, States.BaseState, States.State, { position: 200, duration: 400 }),
                t(States.Hearts.root, States.BaseState, States.State, { position: 200, duration: 400 }),
                t(States.Headphones.root, States.BaseState, States.State, { position: 200, duration: 400 }),
                t(States.Dashboard.root, States.BaseState, States.State, { position: 200, duration: 400 }),

                t(States.Graph.root, States.BaseState, States.State, { position: 200, duration: 400, easing: 'power.out' }),

                t(States.Promotions.root, States.State, States.State2, { position: 605, duration: 1 }),
                t(States.Hearts.root, States.State, States.State2, { position: 605, duration: 1 }),
                t(States.Headphones.root, States.State, States.State2, { position: 605, duration: 1 }),
                t(States.Dashboard.root, States.State, States.State2, { position: 605, duration: 1 }),

                t(States.Graph.root, States.State, States.State2, { position: 650, duration: 400 }),
            ],
        } as const satisfies AnimationEvent;

        export const ScrollResponsive = {
            transitions: [
                t(States.Camera.root, 'Initial', 'Default', { position: 0, duration: 200, easing: 'power.inOut' }),

                t(States.Graph.root, States.BaseState, States.State, { position: 200, duration: 200, easing: 'power.out' }),
                t(States.Graph.root, States.State, States.State2, { position: 400, duration: 400, easing: 'none' }),
            ],
        } as const satisfies AnimationEvent;

        const startTransitionsParams = { duration: 1.5, easing: 'back.out', position: 0.5, reset: true } as const satisfies TransitionArgs;

        export const Start = {
            transitions: [
                t(States.Camera.root, 'Offscreen', 'Initial', { duration: 0.8, position: 0, easing: 'power.out', reset: true }),

                t(States.Hearts.root, States.State, States.BaseState, startTransitionsParams),
                t(States.Graph.root, States.State, States.BaseState, startTransitionsParams),
                t(States.Dashboard.root, States.State, States.BaseState, startTransitionsParams),
                t(States.Promotions.root, States.State, States.BaseState, startTransitionsParams),
                t(States.Headphones.root, States.State, States.BaseState, startTransitionsParams),
            ],
        } as const satisfies AnimationEvent;

        export const StartResponsive = {
            transitions: [
                t(States.Camera.root, 'Offscreen', 'Initial', { duration: 1, position: 0, easing: 'power.out', reset: true }),

                t(States.Graph.root, States.State, States.BaseState, startTransitionsParams),
            ],
        } as const satisfies AnimationEvent;

        const idleTransitionsParams = (duration = 2, delay = 0.5) => ({
            duration,
            easing: 'power2.inOut',
            loop: -1,
            cycle: true,
            delay,
            position: 0,
        } as const satisfies TransitionArgs);

        export const Idle = {
            transitions: [
                t(States.Hearts.float, States.BaseState, States.State, idleTransitionsParams(2.4, 0.5)),
                t(States.Graph.float, States.BaseState, States.State, idleTransitionsParams(3.5)),
                t(States.Dashboard.float, States.BaseState, States.State, idleTransitionsParams(1.8, 1)),
                t(States.Promotions.float, States.BaseState, States.State, idleTransitionsParams(2, 0)),
                t(States.Headphones.float, States.BaseState, States.State, idleTransitionsParams(2.2, 0.75)),
            ],
        } as const satisfies AnimationEvent;

        export const IdleResponsive = {
            transitions: [
                t(States.Graph.float, States.BaseState, States.State, idleTransitionsParams(3.5)),
            ],
        } as const satisfies AnimationEvent;


        function createColorIdleEvent<TData>(map: Record<Colors.Keys, TData>, target: Transition['target'], wrapFactory: (v: TData) => any) {
            const base = {
                target,
                easing: 'power1.inOut',
            };

            const wrapProp = (value: TData) => ((value && wrapFactory) ? wrapFactory(value) : null);

            const tt = (from: TData, to: TData, duration?: number, position?: gsap.Position, reset = false) => ({
                ...base,
                from: wrapProp(from),
                to: wrapProp(to),
                duration,
                position,
                reset,
            }) as const satisfies Transition;

            return {
                transitions: [
                    tt(map.violet, map.green, f(120), 0, true),
                    tt(map.green, map.blue, f(120), f(270)),
                    tt(map.blue, map.orange, f(120), f(540)),
                    tt(map.orange, map.violet, f(120), f(810)),
                    // additional idle
                    tt(null, null, f(150), f(930)),

                    // tt(map.violet, map.violet, f(1080), 0, true),
                ],
            } as const satisfies AnimationEvent;
        }

        export function createBackfaceColorIdleEvent(target: Transition['target']) {
            return createColorIdleEvent(Colors.Backface, target, v => ({ material: { color: v } }));
        }

        export function createBackfaceResetColorEvent(target: Transition['target']) {
            return {
                transitions: [
                    {
                        target,
                        from: null,
                        to: { material: { color: Colors.BackfaceExternal } },
                        duration: 0.3,
                    },
                ],
            } as const satisfies AnimationEvent;
        }

        export function createPrimaryColorIdleEvent(target: Transition['target']) {
            return createColorIdleEvent(Colors.Primary, target, v => ({ material: v }));
        }

        export function createSecondaryColorIdleEvent(target: Transition['target']) {
            return createColorIdleEvent(Colors.Secondary, target, v => ({ material: v }));
        }

        export function createBackgroundColorIdleEvent(target: string | HTMLElement) {
            return createColorIdleEvent(Colors.Background, target, v => ({ 'background-color': v }));
        }

        export namespace Hover {

            const Eases = {
                elastic: 'elastic.out(1, 0.3)',
            };

            const baseArgs = (args?: TransitionArgs): TransitionArgs => ({
                position: 0,
                overwrite: true,
                ...args,
            });

            export const Hearts = {
                transitions: [
                    t(States.Hearts.hover3, States.BaseState, States.State, { position: 0, delay: 0.2, duration: 0.5, easing: 'power2.in', reset: true }),
                    t(States.Hearts.hover3, States.State, States.State2, { position: '>', duration: 0.5, easing: 'power2.out' }),

                    t(States.Hearts.hover2, States.BaseState, States.State, { position: 0, duration: 0.5, easing: 'power2.in', reset: true }),
                    t(States.Hearts.hover2, States.State, States.State2, { position: '>', duration: 0.5, easing: 'power2.out' }),

                    t(States.Hearts.hover1, null, States.State, { position: 0, duration: 0.5, easing: 'power2.inOut' }),
                    t(States.Hearts.hover1, null, States.BaseState, { position: '>', duration: 0.5, easing: 'power2.inOut' }),
                    t(States.Hearts.hover1, null, States.State, { position: '>', duration: 0.5, easing: 'power2.inOut' }),
                    t(States.Hearts.hover1, null, States.BaseState, { position: '>', duration: 0.5, easing: 'power2.inOut' }),
                    t(States.Hearts.hover1, null, States.State, { position: '>', duration: 0.5, easing: 'power2.inOut' }),
                    t(States.Hearts.hover1, null, States.BaseState, { position: '>', duration: 0.5, easing: 'power2.inOut' }),
                ],
            } as const satisfies AnimationEvent;

            export const HeartsOut = {
                transitions: [
                    t(States.Hearts.hover3, null, States.State2, baseArgs({ duration: 0.2, easing: 'power2.in' })),
                    t(States.Hearts.hover2, null, States.State2, baseArgs({ duration: 0.2, easing: 'power2.in' })),
                    t(States.Hearts.hover1, null, States.BaseState, baseArgs({ duration: 0.2, easing: 'power2.out' })),
                ],
            } as const satisfies AnimationEvent;

            export const Headphones = {
                transitions: [
                    t(States.Headphones.hover, null, States.State, baseArgs({ easing: 'back.out', duration: 1.6 })),
                ],
            } as const satisfies AnimationEvent;

            export const HeadphonesOut = {
                transitions: [
                    t(States.Headphones.hover, null, States.BaseState, baseArgs({ easing: 'back.out', duration: 1 })),
                ],
            } as const satisfies AnimationEvent;

            export const Dashboard = {
                transitions: [
                    t(States.Dashboard.hover1, null, States.State, baseArgs({ easing: 'power2.inOut', duration: 0.8 })),
                    t(States.Dashboard.hover2, null, States.State, baseArgs({ easing: Eases.elastic, duration: 1.5 })),
                ],
            } as const satisfies AnimationEvent;

            export const DashboardOut = {
                transitions: [
                    t(States.Dashboard.hover1, null, States.BaseState, baseArgs({ easing: 'power2.inOut', duration: 0.8 })),
                    t(States.Dashboard.hover2, null, States.BaseState, baseArgs({ easing: Eases.elastic, duration: 1 })),
                ],
            } as const satisfies AnimationEvent;

            export const Promotions = {
                transitions: [
                    t(States.Promotions.hover1, null, States.State, baseArgs({ duration: 0.7, easing: 'back.out' })),
                    t(States.Promotions.hover2, null, States.State, baseArgs({ duration: 0.3, easing: 'back.out' })),
                    t(States.Promotions.hover3, null, States.State, baseArgs({ duration: 0.8, easing: 'elastic.out(1, 0.5)' })),
                ],
            } as const satisfies AnimationEvent;

            export const PromotionsOut = {
                transitions: [
                    t(States.Promotions.hover1, null, States.BaseState, baseArgs({ duration: 0.7, easing: 'back.out' })),
                    t(States.Promotions.hover2, null, States.BaseState, baseArgs({ duration: 0.3, easing: 'back.out' })),
                    t(States.Promotions.hover3, null, States.BaseState, baseArgs({ duration: 0.3, easing: 'back.out' })),
                ],
            } as const satisfies AnimationEvent;
        }
    }
}
